import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useToast } from '@kollez/design-system';
import {
  Building2, ChevronRight, CircleAlert, Eye, EyeOff, GraduationCap,
  Hourglass, KeyRound, LogIn, MailOpen, PauseCircle, School, Send,
  ShieldCheck, Sparkles, User,
} from 'lucide-react';
import { apiPost } from '@/lib/api';
import { useAuthStore } from '@/lib/authStore';
import { useRequestSignInLink } from '@/lib/queries';

/**
 * /login — full-featured template port.
 *
 * Faithful React port of the reference template at
 * ~/Downloads/files (16)/login.html — every state, every branch.
 *
 * States: id → { pw | otp | sso | pending | invited | suspended } →
 *              { 2fa } → workspace picker OR locked
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (login uses the reference-template split-with-inkwell-aside).
 */

type Step = 'id' | 'pw' | 'otp' | 'sso' | '2fa' | 'ws' | 'locked' | 'pending' | 'suspended' | 'invited';

interface Workspace { logo: string; name: string; role: string; href: string }

/* ────────── icons ────────── */

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.6 13.2l7.9 6.2C12.4 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17.1z" />
    <path fill="#FBBC05" d="M10.5 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.2C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.9-6.2z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.2-8.5 2.2-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.2C6.6 42.6 14.6 48 24 48z" />
  </svg>
);

/* ────────── aside content — all three sides, always visible ────────── */

interface SideCard {
  key: 'student' | 'partner' | 'institution';
  glyph: string;            // huge background letter (S · P · I)
  eyebrow: string;
  icon: React.ReactNode;
  headline: string;
  proofN: string;           // the ONE big number
  proofLabel: string;       // caption under the number
  cite: string;
  accent: {                 // per-persona color family (rgba strings)
    tint: string;           // card background tint
    edge: string;           // border / accent edge
    text: string;           // chip/label color
    glyph: string;          // the huge background letter color
  };
}

const THREE_SIDES: SideCard[] = [
  {
    key: 'student',
    glyph: 'S',
    eyebrow: 'For students',
    icon: <GraduationCap size={14} />,
    headline: 'Nexie in your pocket. Free forever.',
    proofN: '12,400',
    proofLabel: 'students, this season',
    cite: 'B.Tech CSE → Freshworks',
    accent: {
      tint:  'rgba(232, 212, 122, 0.08)',   // warm chalk-yellow
      edge:  'rgba(232, 212, 122, 0.40)',
      text:  'rgb(232, 212, 122)',
      glyph: 'rgba(232, 212, 122, 0.10)',
    },
  },
  {
    key: 'partner',
    glyph: 'P',
    eyebrow: 'For partners',
    icon: <Building2 size={14} />,
    headline: 'One post, seven campuses. Ranked, cited.',
    proofN: '6w → 3d',
    proofLabel: 'time to hire, Wipro Elite NTH',
    cite: 'Campus TA lead · Wipro Elite NTH',
    accent: {
      tint:  'rgba(240, 138, 138, 0.08)',   // warm terracotta
      edge:  'rgba(240, 138, 138, 0.40)',
      text:  'rgb(240, 168, 158)',
      glyph: 'rgba(240, 138, 138, 0.10)',
    },
  },
  {
    key: 'institution',
    glyph: 'I',
    eyebrow: 'For institutions',
    icon: <School size={14} />,
    headline: 'One register. Fifty colleges. One number at 8 AM.',
    proofN: '87 drives',
    proofLabel: 'up from 34 · Malla Reddy Group',
    cite: 'Director of Placements · 5-college group',
    accent: {
      tint:  'rgba(150, 200, 220, 0.08)',   // cool slate-blue
      edge:  'rgba(150, 200, 220, 0.35)',
      text:  'rgb(180, 215, 232)',
      glyph: 'rgba(150, 200, 220, 0.10)',
    },
  },
];

/* ────────── sub-components ────────── */

function WhoChip({ who, onBack }: { who: string; onBack: () => void }) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full pl-3 pr-1.5 py-1.5 text-sm"
      style={{
        border: '1px solid rgb(var(--line-secondary))',
        background: 'rgb(var(--bg-primary))',
        maxWidth: '100%',
      }}
    >
      <User size={14} style={{ color: 'rgb(var(--ink-tertiary))' }} />
      <span
        className="truncate"
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {who}
      </span>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
        Change
      </button>
    </span>
  );
}

function OtpInput({ value, onChange, autoFocus }: { value: string[]; onChange: (v: string[]) => void; autoFocus?: boolean }) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  function setAt(i: number, v: string) {
    const next = [...value];
    next[i] = v.replace(/\D/g, '').slice(-1);
    onChange(next);
    if (next[i] && refs.current[i + 1]) refs.current[i + 1]?.focus();
  }

  return (
    <div className="otp">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          className="input"
          inputMode="numeric"
          maxLength={1}
          aria-label={`Digit ${i + 1}`}
          value={value[i] ?? ''}
          onChange={(e) => setAt(i, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !value[i] && refs.current[i - 1]) refs.current[i - 1]?.focus();
          }}
          onPaste={(e) => {
            const t = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
            if (t.length === 6) {
              e.preventDefault();
              onChange(t.split(''));
              refs.current[5]?.focus();
            }
          }}
        />
      ))}
    </div>
  );
}

/* ────────── page ────────── */

export function LoginPage() {
  const nav = useNavigate();
  const toast = useToast();
  const { setSession } = useAuthStore();
  const [params] = useSearchParams();

  const [step, setStep] = useState<Step>('id');
  const [ident, setIdent] = useState('');
  const [who, setWho] = useState('');
  const [staff, setStaff] = useState(false);
  const [ws, setWs] = useState<Workspace[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwFails, setPwFails] = useState(0);
  const [busy, setBusy] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const requestSignInLink = useRequestSignInLink();

  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [otpErr, setOtpErr] = useState<string | null>(null);
  const [otp2, setOtp2] = useState<string[]>(['', '', '', '', '', '']);

  const [resendIn, setResendIn] = useState(30);

  // Deep-link support: /login?state=pending etc
  useEffect(() => {
    const s = params.get('state');
    if (s && ['id', 'pw', 'otp', 'sso', '2fa', 'locked', 'pending', 'suspended', 'invited'].includes(s)) {
      if (s === 'invited') setWho('harish.t@git.edu.in');
      else if (s !== 'id') setWho('you@college.edu.in');
      setStep(s as Step);
    }
  }, [params]);

  // Resend countdown
  useEffect(() => {
    if (step !== 'otp' || resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [step, resendIn]);

  function reset() {
    setStep('id');
    setIdent('');
    setWho('');
    setPassword('');
    setPwFails(0);
    setError(null);
    setOtp(['', '', '', '', '', '']);
    setOtp2(['', '', '', '', '', '']);
    setOtpErr(null);
  }

  function submitId(e?: React.FormEvent) {
    e?.preventDefault();
    setError(null);
    const v = ident.trim();
    const digits = v.replace(/[\s-]/g, '').replace(/^\+?91/, '');
    if (/^\d+$/.test(digits)) {
      if (digits.length !== 10 || !/^[6-9]/.test(digits)) {
        setError('Indian mobile numbers have 10 digits and start with 6, 7, 8 or 9.');
        return;
      }
      const w = '+91 ' + digits.slice(0, 5) + ' ' + digits.slice(5);
      setWho(w);
      setStaff(false);
      // Workspaces populated post-OTP from server; empty seed prevents the
      // prior fabricated 'GIT · B.Tech ECE' row from flashing on OTP screen.
      setWs([]);
      setResendIn(30);
      setStep('otp');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(v)) {
      setError(v ? "That doesn't look like an email address or mobile number." : 'Enter your email or mobile number.');
      return;
    }
    const lower = v.toLowerCase();
    setWho(lower);

    if (/godavarigroup\.org$/.test(lower)) { setStep('sso'); return; }
    if (/svgroup/.test(lower)) { setStep('pending'); return; }
    if (/^faculty|harish/.test(lower)) { setStep('invited'); return; }
    if (/coastal/.test(lower)) { setStep('suspended'); return; }

    const isStaff = /edu\.in$/.test(lower) && !/^\d/.test(lower);
    setStaff(isStaff);

    // Workspaces are populated from the /auth/login response inside
    // submitPassword(); no fabricated pattern-matched seed here. The prior
    // block invented GIT/GEG/Wipro rows just from an email prefix.
    setWs([]);
    setStep('pw');
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (password.length < 6) {
        const next = pwFails + 1;
        setPwFails(next);
        setError(next >= 3
          ? `Wrong password. ${5 - next} tries left before a 15-minute lock.`
          : "That password isn't right. Check Caps Lock, or reset it.");
        if (next >= 5) setStep('locked');
        setBusy(false);
        return;
      }
      // Attempt real login (falls back to mock finish on error)
      try {
        const res = await apiPost<{
          user: { id: string; fullName: string; email: string; role: string; tenant: string; onboardingComplete?: boolean };
          tokens: { accessToken: string; refreshToken: string; expiresIn?: number };
        }>('/auth/login', { email: ident.trim(), password });
        setSession(res.user, res.tokens);
        toast.show({ message: 'Signed in' });
        const { landingFor } = await import('@/lib/roleHome');
        setTimeout(() => nav(landingFor(res.user.role), { replace: true }), 400);
        return;
      } catch {
        // Prototype path — fall through to staff 2FA vs finish
        if (staff) setStep('2fa');
        else finishToWorkspace();
      }
    } finally {
      setBusy(false);
    }
  }

  function submitOtp(e: React.FormEvent) {
    e.preventDefault();
    const c = otp.join('');
    if (c.length < 6) { setOtpErr('Enter all 6 digits.'); return; }
    if (c === '000000') {
      setOtpErr("That code has expired. We've sent a new one.");
      setResendIn(30);
      setOtp(['', '', '', '', '', '']);
      return;
    }
    setOtpErr(null);
    finishToWorkspace();
  }

  function submit2fa(e: React.FormEvent) {
    e.preventDefault();
    if (otp2.join('').length < 6) {
      toast.show({ message: 'Enter the 6-digit code from your app' });
      return;
    }
    finishToWorkspace();
  }

  function finishToWorkspace() {
    if (ws.length === 1) {
      toast.show({ message: 'Signed in' });
      const href = ws[0]!.href;
      setTimeout(() => nav(href, { replace: true }), 600);
      return;
    }
    setStep('ws');
  }

  return (
    <div className="auth">
      <div className="auth-main" id="main">
        <div className="auth-card">
          {/* IDENTIFY */}
          {step === 'id' && (
            <section className="grid gap-5">
              <div>
                <h1 className="h-page">Sign in</h1>
                <p className="subhead mt-1">Students, colleges and companies all sign in here.</p>
              </div>
              <button
                type="button"
                className="sso"
                disabled
                title="Google SSO is not yet wired; use email + password. Real OAuth flow lands with the identity-linking backend."
              >
                <GoogleIcon /> Continue with Google · soon
              </button>
              <div className="or">or</div>
              <form className="grid gap-4" onSubmit={submitId} noValidate>
                <label className="grid gap-1.5">
                  <span className="label">Email or mobile number</span>
                  <input
                    className="input"
                    autoComplete="username"
                    placeholder="you@college.edu.in or 98480 22145"
                    autoFocus
                    value={ident}
                    onChange={(e) => setIdent(e.target.value)}
                  />
                  <span
                    className="helper"
                    style={{ color: error ? 'rgb(var(--ink-danger))' : undefined }}
                  >
                    {error ?? 'Students: the mobile number you registered with works best.'}
                  </span>
                </label>
                <button className="btn btn-primary btn-lg" type="submit">Continue</button>
              </form>
              {/* Magic-link alternative — no password. Anti-enumeration:
                  server returns success even for unknown emails, so the
                  after-send copy is the same in both cases. */}
              {magicLinkSent ? (
                <div className="notice notice-success" data-testid="magic-link-sent">
                  <MailOpen size={14} />
                  <div>
                    If <b>{ident}</b> matches an account, we've sent a sign-in link.
                    Check your inbox — it works once and expires in 15 minutes.
                  </div>
                </div>
              ) : (
                <div className="grid gap-2">
                  <div className="or">or</div>
                  <button
                    type="button"
                    className="btn btn-outline btn-lg"
                    data-testid="magic-link-request"
                    disabled={!ident.trim() || !/^\S+@\S+\.\S+$/.test(ident.trim()) || requestSignInLink.isPending}
                    onClick={async () => {
                      try {
                        await requestSignInLink.mutateAsync(ident.trim());
                        setMagicLinkSent(true);
                      } catch {
                        setMagicLinkSent(true);
                      }
                    }}
                  >
                    <Send size={14} />
                    {requestSignInLink.isPending ? 'Sending…' : 'Email me a sign-in link'}
                  </button>
                  <p className="helper m-0" style={{ fontSize: '.8125rem' }}>
                    No password? We'll email a one-time link — no need to type your password.
                  </p>
                </div>
              )}
              <p className="text-sm m-0">
                New to kollez? <Link className="link" to="/register">Create an account</Link>
                {' '}· Invited by your college? <Link className="link" to="/accept-invite">Accept invitation</Link>
              </p>
            </section>
          )}

          {/* PASSWORD */}
          {step === 'pw' && (
            <section className="grid gap-5">
              <div className="grid gap-3">
                <h1 className="h-page">Enter your password</h1>
                <WhoChip who={who} onBack={reset} />
              </div>
              <form className="grid gap-4" onSubmit={submitPassword} noValidate>
                <label className="grid gap-1.5">
                  <span className="flex justify-between">
                    <span className="label">Password</span>
                    <Link className="link text-sm" to="/forgot-password">Forgot?</Link>
                  </span>
                  <div className="input-group">
                    <input
                      className="input"
                      type={showPw ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{ paddingLeft: 12 }}
                    />
                    <button
                      type="button"
                      className="input-addon"
                      onClick={() => setShowPw((s) => !s)}
                      style={{ borderLeft: 0, borderRight: '1px solid rgb(var(--line-secondary))', borderRadius: '0 7px 7px 0', cursor: 'pointer' }}
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                    >
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {error && (
                    <span className="helper" style={{ color: 'rgb(var(--ink-danger))' }}>{error}</span>
                  )}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="check" defaultChecked /> Keep me signed in on this device
                </label>
                <button className="btn btn-primary btn-lg" type="submit" disabled={busy}>
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>
                <button
                  className="btn btn-ghost"
                  type="button"
                  onClick={() => toast.show({ message: `Sign-in link sent to ${who}. It works once, for 15 minutes.` })}
                >
                  <MailOpen size={14} className="mr-1" /> Email me a sign-in link instead
                </button>
              </form>
            </section>
          )}

          {/* OTP */}
          {step === 'otp' && (
            <section className="grid gap-5">
              <div className="grid gap-3">
                <h1 className="h-page">Enter the code</h1>
                <p className="subhead m-0">We sent a 6-digit code by SMS and WhatsApp to <b>{who}</b>.</p>
              </div>
              <form className="grid gap-4" onSubmit={submitOtp} noValidate>
                <OtpInput value={otp} onChange={setOtp} autoFocus />
                <span
                  className="helper"
                  style={{ color: otpErr ? 'rgb(var(--ink-danger))' : undefined }}
                >
                  {otpErr ?? 'It fills in by itself on most Android phones.'}
                </span>
                <button className="btn btn-primary btn-lg" type="submit">Verify and sign in</button>
                <div className="flex justify-between text-sm">
                  <button type="button" className="link" onClick={reset}>Use a different number</button>
                  <button
                    type="button"
                    className="link"
                    disabled={resendIn > 0}
                    onClick={() => { setResendIn(30); toast.show({ message: 'New code sent' }); }}
                  >
                    {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                  </button>
                </div>
              </form>
            </section>
          )}

          {/* SSO REQUIRED */}
          {step === 'sso' && (
            <section className="grid gap-5">
              <div className="grid gap-3">
                <h1 className="h-page">Sign in with your institution</h1>
                <WhoChip who={who} onBack={reset} />
              </div>
              <p className="m-0">
                Your institution is set up to sign in through Google Workspace. That flow is being wired now —
                for the pilot, sign in with email + password from the previous screen.
              </p>
              <button
                type="button"
                className="sso"
                disabled
                title="Google Workspace SSO lands with the identity-linking backend."
              >
                <LogIn size={16} /> Continue to Google Workspace · soon
              </button>
              <p className="helper m-0">
                Can&apos;t get in? Your institution&apos;s IT team manages this account, not kollez.
              </p>
            </section>
          )}

          {/* 2FA */}
          {step === '2fa' && (
            <section className="grid gap-5">
              <div className="grid gap-2">
                <span
                  className="logo-tile"
                  style={{ width: 44, height: 44 }}
                >
                  <ShieldCheck size={20} />
                </span>
                <h1 className="h-page">Two-step check</h1>
                <p className="subhead m-0">
                  Staff accounts can see student records, so we ask for a code from your authenticator app.
                </p>
              </div>
              <form className="grid gap-4" onSubmit={submit2fa} noValidate>
                <OtpInput value={otp2} onChange={setOtp2} autoFocus />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="check" defaultChecked />
                  Don&apos;t ask again on this device for 30 days
                </label>
                <button className="btn btn-primary btn-lg" type="submit">Verify</button>
                <div className="flex justify-between flex-wrap gap-2 text-sm">
                  <button
                    type="button"
                    className="link"
                    onClick={() => toast.show({ message: 'Code sent by SMS to +91 98•••• ••21' })}
                  >
                    Text me a code instead
                  </button>
                  <button
                    type="button"
                    className="link"
                    onClick={() => toast.show({ message: 'Enter one of your 10 backup codes in the boxes. Each works once.' })}
                  >
                    <KeyRound size={12} className="inline mr-1" />
                    Use a backup code
                  </button>
                </div>
              </form>
            </section>
          )}

          {/* WORKSPACE PICKER */}
          {step === 'ws' && (
            <section className="grid gap-4">
              <div>
                <h1 className="h-page">Where to?</h1>
                <p className="subhead mt-1">You have access to more than one place. You can switch any time from the top-left menu.</p>
              </div>
              <div className="grid gap-2">
                {ws.map((w, i) => (
                  <button
                    key={i}
                    type="button"
                    className="grid gap-3 items-center px-3.5 py-3 text-left"
                    style={{
                      gridTemplateColumns: '40px minmax(0,1fr) auto',
                      border: '1px solid rgb(var(--line-secondary))',
                      borderRadius: 10,
                      background: 'rgb(var(--bg-primary))',
                      cursor: 'pointer',
                    }}
                    onClick={() => nav(w.href, { replace: true })}
                  >
                    <span className="logo-tile">{w.logo}</span>
                    <span className="min-w-0">
                      <span className="cell-title block truncate">{w.name}</span>
                      <span className="cell-sub">
                        {w.role}{i === 0 ? ' · last used' : ''}
                      </span>
                    </span>
                    <ChevronRight size={16} />
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* LOCKED */}
          {step === 'locked' && (
            <section className="grid gap-4">
              <span
                className="logo-tile"
                style={{ width: 44, height: 44, color: 'rgb(var(--ink-danger))' }}
              >
                <CircleAlert size={20} />
              </span>
              <h1 className="h-page">Account locked for 15 minutes</h1>
              <p className="m-0">
                There were 5 wrong passwords in a row, so we paused sign-in to protect the student records this account can see. We&apos;ve emailed you about it.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Link className="btn btn-primary" to="/forgot-password">Reset password now</Link>
                <button className="btn btn-ghost" type="button" onClick={reset}>Try another account</button>
              </div>
              <p className="helper m-0">Wasn&apos;t you? Reset your password and tell your admin.</p>
            </section>
          )}

          {/* PENDING */}
          {step === 'pending' && (
            <section className="grid gap-4">
              <span
                className="logo-tile"
                style={{ width: 44, height: 44, color: 'rgb(var(--ink-warning))' }}
              >
                <Hourglass size={20} />
              </span>
              <h1 className="h-page">We&apos;re still checking your sign-up</h1>
              <p className="m-0">
                Sri Venkateswara Group was registered yesterday. We verify institutions before any student data can be added — usually within one working day.
              </p>
              <ol className="timeline">
                <li data-state="done">
                  <span className="t-dot" />
                  <div className="t-title">GSTIN and colleges matched</div>
                  <div className="t-meta">16 Sept</div>
                </li>
                <li data-state="current">
                  <span className="t-dot" />
                  <div className="t-title">Authorisation letter being reviewed</div>
                  <div className="t-meta">By Kiran, kollez onboarding</div>
                </li>
                <li>
                  <span className="t-dot" />
                  <div className="t-title">You get a sign-in email</div>
                  <div className="t-meta">Then invite your placement officers</div>
                </li>
              </ol>
              <div className="flex gap-2 flex-wrap">
                <Link className="btn btn-outline" to="/contact?topic=onboarding">Talk to onboarding</Link>
                <button className="btn btn-ghost" type="button" onClick={reset}>Back</button>
              </div>
            </section>
          )}

          {/* SUSPENDED */}
          {step === 'suspended' && (
            <section className="grid gap-4">
              <span
                className="logo-tile"
                style={{ width: 44, height: 44, color: 'rgb(var(--ink-danger))' }}
              >
                <PauseCircle size={20} />
              </span>
              <h1 className="h-page">Access is paused for your college</h1>
              <p className="m-0">
                Your institution&apos;s kollez account is on hold. Your data is safe and nothing has been deleted. Your placement office or administrator can restore access.
              </p>
              <p className="helper m-0">
                Students: your applications and offers are kept. If you have an offer deadline, contact the company directly — the letter has their details.
              </p>
              <button className="btn btn-ghost justify-self-start" type="button" onClick={reset}>Back</button>
            </section>
          )}

          {/* INVITED */}
          {step === 'invited' && (
            <section className="grid gap-4">
              <span
                className="logo-tile"
                style={{ width: 44, height: 44 }}
              >
                <MailOpen size={20} />
              </span>
              <h1 className="h-page">You have an invitation waiting</h1>
              <p className="m-0">
                Prof. Suresh B. invited <b>{who || 'harish.t@git.edu.in'}</b> to GIT Rajahmundry as a faculty coordinator. Accept it to set your password.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Link className="btn btn-primary" to="/accept-invite">Accept invitation</Link>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => toast.show({ message: `Invitation sent again to ${who}` })}
                >
                  <Send size={14} className="mr-1" /> Resend the email
                </button>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* STORY ASIDE — three tactile persona cards, each with distinct color + giant glyph */}
      <aside
        className="auth-side"
        style={{
          // Left padding must clear .auth-side::before (the red margin rule at left: 56px, width 2px).
          // 96px leaves comfortable clearance so cards don't overlap the rule.
          padding: '48px 56px 48px 96px',
          alignContent: 'center',
        }}
      >
        <div
          className="grid w-full"
          style={{ maxWidth: 440, alignSelf: 'center', gap: 18 }}
        >
          {/* Compact header — small, gets out of the way */}
          <div className="flex items-baseline justify-between">
            <p
              className="m-0 uppercase font-medium flex items-center gap-2"
              style={{
                fontSize: '.6875rem',
                letterSpacing: '.16em',
                color: 'rgba(255,255,255,0.55)',
              }}
            >
              <Sparkles size={11} style={{ color: 'rgb(232,212,122)' }} />
              Three sides · one register
            </p>
            <p
              className="m-0 tabular-nums"
              style={{ fontSize: '.6875rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '.1em' }}
            >
              {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }).toUpperCase()}
            </p>
          </div>

          {/* THREE PERSONA CARDS — each tactile, distinct color, giant background glyph */}
          <div className="grid" style={{ gap: 14 }}>
            {THREE_SIDES.map((s) => (
              <article
                key={s.key}
                className="relative overflow-hidden"
                style={{
                  borderRadius: 12,
                  background: s.accent.tint,
                  border: `1px solid ${s.accent.edge}`,
                  padding: '14px 18px',
                  minHeight: 108,
                }}
              >
                {/* Giant background glyph */}
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    right: -12,
                    top: -32,
                    fontFamily: 'Newsreader, Georgia, serif',
                    fontSize: 160,
                    fontWeight: 500,
                    lineHeight: 1,
                    color: s.accent.glyph,
                    letterSpacing: '-0.05em',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                >
                  {s.glyph}
                </span>

                {/* Foreground content */}
                <div className="relative" style={{ zIndex: 1 }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="flex items-center justify-center rounded"
                      style={{
                        width: 22, height: 22,
                        background: s.accent.edge,
                        color: 'rgba(20,20,30,0.9)',
                      }}
                    >
                      {s.icon}
                    </span>
                    <span
                      className="uppercase font-medium"
                      style={{
                        fontSize: '.6875rem',
                        letterSpacing: '.14em',
                        color: s.accent.text,
                      }}
                    >
                      {s.eyebrow}
                    </span>
                  </div>

                  <h3
                    className="m-0 mb-3"
                    style={{
                      fontFamily: 'Newsreader, Georgia, serif',
                      fontSize: '.9375rem',
                      lineHeight: 1.25,
                      fontWeight: 500,
                      color: '#fff',
                      letterSpacing: '-0.005em',
                      maxWidth: '22ch',
                    }}
                  >
                    {s.headline}
                  </h3>

                  {/* The one big proof */}
                  <div className="flex items-baseline gap-3">
                    <span
                      className="font-serif tabular-nums"
                      style={{
                        fontSize: '1.5rem',
                        fontWeight: 500,
                        lineHeight: 1,
                        color: s.accent.text,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {s.proofN}
                    </span>
                    <span
                      style={{
                        fontSize: '.75rem',
                        color: 'rgba(255,255,255,0.6)',
                        lineHeight: 1.35,
                        maxWidth: '18ch',
                      }}
                    >
                      {s.proofLabel}
                    </span>
                  </div>

                  <p
                    className="m-0 mt-2 italic"
                    style={{
                      fontFamily: 'Newsreader, Georgia, serif',
                      fontSize: '.75rem',
                      color: 'rgba(255,255,255,0.5)',
                    }}
                  >
                    — {s.cite}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {/* Compact closing line + trust chips — small, tertiary */}
          <div className="grid gap-3">
            <p
              className="m-0 italic"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: '.875rem',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.45,
              }}
            >
              One AI, one platform, one register.
            </p>
            <ul
              className="flex flex-wrap gap-x-3 gap-y-1"
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                fontSize: '.6875rem',
                color: 'rgba(255,255,255,0.4)',
                letterSpacing: '.06em',
              }}
              aria-label="Trust signals"
            >
              <li className="flex items-center gap-1">
                <ShieldCheck size={11} style={{ color: 'rgb(232,212,122)' }} />
                DPDP Act 2023
              </li>
              <li>·</li>
              <li>SOC 2 Type II</li>
              <li>·</li>
              <li>ISO 27001</li>
              <li>·</li>
              <li>India-hosted</li>
            </ul>
          </div>
        </div>
      </aside>
    </div>
  );
}
